// Date-range filter helpers.
//
// Record dates (req_entry / req_completion / tp_entry) are dd/mm/yy strings. The filter bar stores
// the range as ISO yyyy-mm-dd strings (the native <input type="date"> format).
// Which record date a range applies to depends on the tab's quarter mode (ADR 0010).
// An empty string / undefined end means "open-ended". Both ends are inclusive.

export interface DateRangeState {
  dateFrom?: string;
  dateTo?: string;
}

function localMidnight(y: number, m: number, d: number): number | null {
  const dt = new Date(y, m - 1, d);
  // Reject overflow such as 31/02 (Date would silently roll it into March).
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt.getTime();
}

/** Parse a dd/mm/yy (or dd/mm/yyyy) record date to local-midnight ms, or null when blank/invalid. */
export function parseEntryDate(entry: string | null | undefined): number | null {
  if (!entry) return null;
  const token = entry.trim().split(/[\sT]/)[0];
  const parts = token.split('/');
  if (parts.length !== 3) return null;
  const [dd, mm, yy] = parts.map(Number);
  if (![dd, mm, yy].every(Number.isInteger)) return null;
  const year = yy < 100 ? (yy > 50 ? 1900 + yy : 2000 + yy) : yy;
  return localMidnight(year, mm, dd);
}

/** Parse a yyyy-mm-dd string to local-midnight ms, or null when blank/invalid. */
export function parseIsoDate(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return localMidnight(Number(m[1]), Number(m[2]), Number(m[3]));
}

/** yyyy-mm-dd -> dd/mm/yy for display (matches how record dates are shown). */
export function formatIsoAsDdMmYy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
}

export function hasDateRange(s: DateRangeState): boolean {
  return !!(s.dateFrom || s.dateTo);
}

/**
 * True when no range is set, or when the record date falls within it (inclusive).
 * With a range active, records with a blank/unparseable date never match.
 */
export function matchesDateRange(entry: string | null | undefined, s: DateRangeState): boolean {
  if (!hasDateRange(s)) return true;
  const t = parseEntryDate(entry);
  if (t === null) return false;
  const from = parseIsoDate(s.dateFrom);
  const to = parseIsoDate(s.dateTo);
  if (from !== null && t < from) return false;
  if (to !== null && t > to) return false;
  return true;
}
